<template>
  <span
    class="bdx-export-icon-btn ml-2"
    role="button"
    :title="isExporting ? 'Exporting…' : 'Download Excel report'"
    @click="!isExporting && handleExport()"
  >
    <span v-if="isExporting" class="bdx-btn-spinner" />
    <b-icon v-else icon="arrow-down" :font-scale="0.9" />
  </span>
</template>

<script>
import { defineComponent, inject, ref } from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import * as XLSX from 'xlsx'

import { LincolnNewGlobalUMRService } from '@services'
import { useTryCatch, useToast, useCurrentUser, useUserCompany } from '@composables'
import { extractBdxRows } from '@util'

import { BaseIcon } from '@components/base'

import { BDXMatchPendingManagerInjectionKey } from './BDXMatchPendingManager.constants'

const LIST_COLUMNS = [
  { key: 'umr',        label: 'UMR' },
  { key: 'risk_code',  label: 'Risk Code' },
  { key: 'cat_code',   label: 'Catastrophe Code' },
  { key: 'section_no', label: 'Section Number' }
]

const PREVIEW_COLUMNS = [
  { key: 'TPA_Name',                  label: 'TPA' },
  { key: 'Coverholder_Name',          label: 'Coverholder' },
  { key: 'Settlement_Currency',       label: 'Settlement Currency' },
  { key: 'Year_of_Account',           label: 'YOA' },
  { key: 'Binder_Contract_Inception', label: 'Binder Inception' }
]

const ALL_COLUMNS = [...LIST_COLUMNS, ...PREVIEW_COLUMNS]

export default defineComponent({
  name: 'BDXMatchPendingManagerExportButton',
  components: { BaseIcon },
  setup() {
    const { useGetters } = createVuexHelpers()
    const { bdxFilters } = useGetters(['bdxFilters'])

    const { isSuperUser } = useCurrentUser()
    const { id: userCompanyId } = useUserCompany()

    const { search } = inject(BDXMatchPendingManagerInjectionKey)

    const { showToast } = useToast()
    const isExporting = ref(false)

    const handleExport = async () => {
      isExporting.value = true

      await useTryCatch(async () => {
        const companyId = isSuperUser.value
          ? bdxFilters.value.companyId
          : userCompanyId.value

        const { data } = await LincolnNewGlobalUMRService.list(companyId, {
          search: search.value,
          page_size: 1000
        })
        const items = (data || {}).results || []

        if (!items.length) {
          showToast('No records available to export.')
          return
        }

        const BATCH = 10
        const allRows = []
        for (let i = 0; i < items.length; i += BATCH) {
          const batchResults = await Promise.all(
            items.slice(i, i + BATCH).map(async (item) => {
              try {
                const response = await LincolnNewGlobalUMRService.getBdxDataRows({
                  umr_rc_cc_sn: item.umr_rc_cc_sn || ''
                })
                return extractBdxRows(response.data).map((row) =>
                  ALL_COLUMNS.map(({ key }) => {
                    if (LIST_COLUMNS.find((c) => c.key === key)) {
                      return item[key] != null ? item[key] : ''
                    }
                    const v = row[key]
                    if (v == null) return ''
                    return key === 'Year_of_Account' ? String(v) : v
                  })
                )
              } catch {
                return [ALL_COLUMNS.map(({ key }) =>
                  LIST_COLUMNS.find((c) => c.key === key) ? (item[key] != null ? item[key] : '') : ''
                )]
              }
            })
          )
          batchResults.forEach((rows) => allRows.push(...rows))
        }

        const headers = ALL_COLUMNS.map(({ label }) => label)
        const ws = XLSX.utils.aoa_to_sheet([headers, ...allRows])

        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'BDX Match Pending')
        const now = new Date()
        const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`
        XLSX.writeFile(wb, `BDX Match Pending ${ts}.xlsx`)
      })

      isExporting.value = false
    }

    return { isExporting, handleExport }
  }
})
</script>

<style scoped>
.bdx-export-icon-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: var(--primary);
  color: #fff;
  cursor: pointer;
  transition: opacity 0.15s;
}
.bdx-export-icon-btn:hover { opacity: 0.85; }
.bdx-export-icon-btn[data-exporting] { cursor: not-allowed; opacity: 0.75; }

.bdx-btn-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: bdx-spin 0.7s linear infinite;
}
@keyframes bdx-spin { to { transform: rotate(360deg); } }
</style>
